import { yupResolver } from "@hookform/resolvers/yup"
import { type ReactNode, useEffect, useState } from "react"
import {
  type DefaultValues,
  type FieldErrors,
  type FieldValues,
  FormProvider,
  type Resolver,
  type UnpackNestedValue,
  type UseFormReturn,
  useForm,
} from "react-hook-form"
import { useToaster } from "../../../feedback"
import { PaperContent } from "../../../layout"
import { type Tab, TabbedPanel, type TabbedPanelProps } from "../../../navigation"
import type { YupTypes } from "../../yup-custom"
import { DirtyExitDialog } from "../DirtyExitDialog"
import { FormHeader, type FormHeaderProps } from "../FormHeader"
import { useTabsValidation } from "./useTabsValidation"

export interface FormTab extends Tab {
  schema: YupTypes.AnyObjectSchema
}

export interface TabbedFormProps<T extends FieldValues, C = any> {
  tabs: FormTab[]
  initialValues?: DefaultValues<T>
  onSubmit: (
    data: UnpackNestedValue<T>,
    formMethods: UseFormReturn<T, C>,
  ) => Promise<void> | void
  onError?: (
    errors: FieldErrors<T>,
    formMethods: UseFormReturn<T, C>,
  ) => Promise<void> | void
  onCancel?: () => void
  /** Props passed to each tab component */
  RouteElementProps?: Record<string, unknown>
  /** Show a spinner */
  loading?: boolean
  /** Context sent to tabs schemas */
  context?: C
  tabsRightChildren?: TabbedPanelProps["tabsRightChildren"]
  topBanner?: ReactNode

  // Header
  title: FormHeaderProps["title"]
  chip?: FormHeaderProps["chip"]
  submitLabel?: FormHeaderProps["submitLabel"]
  chipLabel?: string
  headerActions?: FormHeaderProps["otherActions"]
  hideActions?: boolean
}

/**
 * Ensure on all domains:
 * - Consistent validation rules (per tab validation w/ routing)
 * - Consistent implementation of ReactRouter, ReactHookForm, MUI
 * - Consistent UX
 */
export const TabbedForm = <T extends FieldValues, C = any>({
  tabs,
  initialValues,
  onSubmit,
  onError,
  onCancel,
  RouteElementProps,
  loading = false,
  context,
  tabsRightChildren,
  topBanner,
  // Header
  title,
  chip,
  chipLabel,
  submitLabel,
  headerActions,
  hideActions = false,
}: TabbedFormProps<T, C>) => {
  const toaster = useToaster()

  // Validation
  const { currentTabs, schema, validateTabs, resetInvalidTabs } = useTabsValidation({
    tabs,
  })

  // Form
  const formMethods = useForm<T, C>({
    resolver: yupResolver(schema) as unknown as Resolver<T, C>,
    context,
    reValidateMode: "onBlur",
  })
  const { isSubmitting, isDirty } = formMethods.formState

  useEffect(() => {
    if (initialValues) formMethods.reset(initialValues)
  }, [formMethods, initialValues])

  const [submitLoading, setSubmitLoading] = useState(false)
  const formSubmit = formMethods.handleSubmit(
    async (formValues) => {
      setSubmitLoading(true)
      resetInvalidTabs()

      try {
        await onSubmit(formValues, formMethods)
        validateTabs(formMethods.formState.errors)
      } catch (err) {
        console.error("Submit action error", err)
        toaster.error("Une erreur est survenue")
      } finally {
        setSubmitLoading(false)
      }
    },
    (formErrors) => {
      console.error("Errors in form:", {
        errors: formErrors,
        values: formMethods.getValues(),
      })
      toaster.error("Veuillez remplir correctement le formulaire")

      validateTabs(formErrors)

      if (onError) {
        onError(formErrors, formMethods)
      }
    },
  )

  return (
    <>
      {/* HEADER */}
      <FormHeader
        title={title}
        chip={chip}
        onCancel={hideActions ? undefined : onCancel}
        submitLabel={submitLabel}
        submitLoading={submitLoading}
        onSubmit={hideActions ? undefined : formSubmit}
        otherActions={hideActions ? [] : headerActions}
      />

      {/* CONTENT */}
      <PaperContent
        loading={loading}
        topBanner={topBanner}
        sx={{ overflow: "visible", position: "relative" }}
      >
        <FormProvider {...formMethods}>
          <TabbedPanel
            tabs={currentTabs}
            RouteElementProps={RouteElementProps}
            tabsRightChildren={tabsRightChildren}
            chipLabel={chipLabel}
          />
        </FormProvider>
      </PaperContent>

      {/* DIALOGS */}
      <DirtyExitDialog
        when={!isSubmitting && isDirty}
        allowSameLevelRouting={tabs.length > 1}
      />
    </>
  )
}
